import React, { useState, useEffect, useRef } from 'react';
import './CalendarWithTemplates.css';

const CalendarWithTemplates = ({ selectedIndustry = 'medical' }) => {
  const [templatePreview, setTemplatePreview] = useState(null);
  const previewRef = useRef(null);

  // Mock template data for healthcare industries only
  const getIndustryTemplates = (industry) => {
    const templates = {
      medical: {
        // December 10th - Scheduled campaign
        '10': {
          id: 'medical-1',
          name: 'Holiday Wellness Campaign',
          type: 'Seasonal Promotion',
          description: 'Holiday-themed wellness packages to boost end-of-year appointments',
          preview: {
            color: '#ef4444',
            icon: '🎄',
            features: ['Holiday Discounts', 'Wellness Packages', 'Gift Certificates', 'Referral Program']
          },
          metrics: {
            expectedReach: '2,500 households',
            responseRate: '8.2%',
            avgAppointmentValue: '$285'
          }
        },
        // December 15th - Optimal campaign
        '15': {
          id: 'medical-2',
          name: 'Winter Health Check',
          type: 'Preventive Care',
          description: 'Comprehensive winter health screening and flu vaccination campaign',
          preview: {
            color: '#10b981',
            icon: '❄️',
            features: ['Flu Shots', 'Annual Checkups', 'Vitamin D Testing', 'Cold Weather Tips']
          },
          metrics: {
            expectedReach: '3,200 households',
            responseRate: '12.5%',
            avgAppointmentValue: '$195'
          }
        },
        // December 20th - Optimal campaign
        '20': {
          id: 'medical-3',
          name: 'New Year Resolution',
          type: 'Lifestyle Medicine',
          description: 'New Year weight management and lifestyle medicine campaign',
          preview: {
            color: '#3b82f6',
            icon: '🎯',
            features: ['Weight Management', 'Nutrition Counseling', 'Exercise Programs', 'Lifestyle Coaching']
          },
          metrics: {
            expectedReach: '2,800 households',
            responseRate: '9.8%',
            avgAppointmentValue: '$320'
          }
        }
      },
      dental: {
        '10': {
          id: 'dental-1',
          name: 'Holiday Smile Special',
          type: 'Cosmetic Dentistry',
          description: 'Holiday whitening and cosmetic dentistry packages for the perfect smile',
          preview: {
            color: '#3b82f6',
            icon: '✨',
            features: ['Teeth Whitening', 'Cosmetic Consultations', 'Holiday Gift Cards', 'New Patient Specials']
          },
          metrics: {
            expectedReach: '6,100 households',
            responseRate: '11.3%',
            avgAppointmentValue: '$420'
          }
        },
        '15': {
          id: 'dental-2',
          name: 'Winter Oral Health Check',
          type: 'Preventive Care',
          description: 'Comprehensive winter dental checkups and cleaning promotions',
          preview: {
            color: '#10b981',
            icon: '🦷',
            features: ['Professional Cleaning', 'X-rays Included', 'Gum Health Check', 'Fluoride Treatment']
          },
          metrics: {
            expectedReach: '7,800 households',
            responseRate: '14.2%',
            avgAppointmentValue: '$165'
          }
        },
        '20': {
          id: 'dental-3',
          name: 'New Year Smile Makeover',
          type: 'Restorative Dentistry',
          description: 'Comprehensive smile makeover packages for the new year',
          preview: {
            color: '#8b5cf6',
            icon: '🌟',
            features: ['Veneers & Crowns', 'Orthodontic Consultations', 'Dental Implants', 'Full Mouth Restoration']
          },
          metrics: {
            expectedReach: '3,900 households',
            responseRate: '7.8%',
            avgAppointmentValue: '$1,250'
          }
        }
      }
    };

    return templates[industry] || templates.medical;
  };

  const mockTemplates = getIndustryTemplates(selectedIndustry);

  const handleDateClick = (date) => {
    console.log('Clicking on date:', date);
    if (mockTemplates[date]) {
      console.log('Template found for date:', date, mockTemplates[date]);
      // Toggle the popup - if it's already showing for this date, close it
      if (templatePreview && templatePreview.preview.title === mockTemplates[date].preview.title) {
        setTemplatePreview(null);
      } else {
        setTemplatePreview(mockTemplates[date]);
      }
    } else {
      console.log('No template found for date:', date);
      setTemplatePreview(null);
    }
  };

  // Get template color for CSS variable
  const templateColor = templatePreview ? templatePreview.preview.color : '#10b981';

  const handleClosePreview = () => {
    setTemplatePreview(null);
  };

  // Handle outside click to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (previewRef.current && !previewRef.current.contains(event.target)) {
        setTemplatePreview(null);
      }
    };

    if (templatePreview) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [templatePreview]);

  const getIndustryIcon = (industry) => {
    const icons = {
      medical: '🏥',
      dental: '🦷'
    };
    return icons[industry] || '🏥';
  };

  const getIndustryName = (industry) => {
    const names = {
      medical: 'Medical Practice',
      dental: 'Dentistry'
    };
    return names[industry] || 'Medical Practice';
  };

  return (
    <div className="calendar-container">
      <div className="calendar-widget">
        <div className="calendar-header">
          <h4>{getIndustryIcon(selectedIndustry)} {getIndustryName(selectedIndustry)} Calendar</h4>
          <span className="month-year">December 2024</span>
        </div>
        <div className="calendar-grid">
          <div className="calendar-days">
            <div className="day-header">Sun</div>
            <div className="day-header">Mon</div>
            <div className="day-header">Tue</div>
            <div className="day-header">Wed</div>
            <div className="day-header">Thu</div>
            <div className="day-header">Fri</div>
            <div className="day-header">Sat</div>
          </div>
          <div className="calendar-dates">
            {/* Empty dates for proper alignment */}
            <div className="date"></div>
            <div className="date"></div>
            <div className="date"></div>

            {/* Actual dates */}
            <div className="date" onClick={() => handleDateClick('1')}>1</div>
            <div className="date" onClick={() => handleDateClick('2')}>2</div>
            <div className="date" onClick={() => handleDateClick('3')}>3</div>
            <div className="date" onClick={() => handleDateClick('4')}>4</div>
            <div className="date" onClick={() => handleDateClick('5')}>5</div>
            <div className="date" onClick={() => handleDateClick('6')}>6</div>
            <div className="date" onClick={() => handleDateClick('7')}>7</div>
            <div className="date" onClick={() => handleDateClick('8')}>8</div>
            <div className="date" onClick={() => handleDateClick('9')}>9</div>
            <div className="date scheduled" onClick={() => handleDateClick('10')}>10</div>
            <div className="date" onClick={() => handleDateClick('11')}>11</div>
            <div className="date" onClick={() => handleDateClick('12')}>12</div>
            <div className="date" onClick={() => handleDateClick('13')}>13</div>
            <div className="date" onClick={() => handleDateClick('14')}>14</div>
            <div className="date optimal" onClick={() => handleDateClick('15')}>15</div>
            <div className="date" onClick={() => handleDateClick('16')}>16</div>
            <div className="date" onClick={() => handleDateClick('17')}>17</div>
            <div className="date" onClick={() => handleDateClick('18')}>18</div>
            <div className="date" onClick={() => handleDateClick('19')}>19</div>
            <div className="date optimal" onClick={() => handleDateClick('20')}>20</div>
            <div className="date" onClick={() => handleDateClick('21')}>21</div>
            <div className="date" onClick={() => handleDateClick('22')}>22</div>
            <div className="date" onClick={() => handleDateClick('23')}>23</div>
            <div className="date" onClick={() => handleDateClick('24')}>24</div>
            <div className="date" onClick={() => handleDateClick('25')}>25</div>
            <div className="date" onClick={() => handleDateClick('26')}>26</div>
            <div className="date" onClick={() => handleDateClick('27')}>27</div>
            <div className="date" onClick={() => handleDateClick('28')}>28</div>
            <div className="date" onClick={() => handleDateClick('29')}>29</div>
            <div className="date" onClick={() => handleDateClick('30')}>30</div>
            <div className="date" onClick={() => handleDateClick('31')}>31</div>
          </div>
        </div>

        {/* Legend */}
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-dot scheduled"></div>
            <span>Scheduled Campaign (Click to view)</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot optimal"></div>
            <span>Optimal Date (Click to view)</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot available"></div>
            <span>Available</span>
          </div>
        </div>
      </div>

      {/* Template Preview Tooltip */}
      {templatePreview && (
        <div
          ref={previewRef}
          className="template-preview"
          style={{ '--template-color': templateColor }}
        >
          <button className="template-close" onClick={handleClosePreview}>×</button>
          <div className="template-header">
            <div className="template-icon" style={{ backgroundColor: templatePreview.preview.color }}>
              {templatePreview.preview.icon}
            </div>
            <div className="template-info">
              <h3 className="template-title">{templatePreview.name}</h3>
              <span className="template-type">{templatePreview.type}</span>
            </div>
          </div>

          <p className="template-description">{templatePreview.description}</p>

          <div className="template-features">
            <h4>Campaign Features:</h4>
            <div className="features-list">
              {templatePreview.preview.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="template-metrics">
            <div className="metric-row">
              <span>Expected Reach:</span>
              <span className="metric-value">{templatePreview.metrics.expectedReach}</span>
            </div>
            <div className="metric-row">
              <span>Response Rate:</span>
              <span className="metric-value">{templatePreview.metrics.responseRate}</span>
            </div>
            <div className="metric-row">
              <span>Avg. Appointment Value:</span>
              <span className="metric-value">{templatePreview.metrics.avgAppointmentValue}</span>
            </div>
          </div>

          <div className="template-actions">
            <button className="template-btn primary">Schedule Campaign</button>
            <button className="template-btn secondary">View Details</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarWithTemplates;
